import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterDropdownControl } from '../../../../shared/dynamic-forms/form-models/filter-dropdown-control';
import { isTrueValue } from '../../../data-helpers/type-check-helper';
import { FormControlBase } from '../../form-models/form-control-base';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'df-control',
  templateUrl: './dynamic-form-control.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class DynamicFormControlComponent implements OnInit, OnDestroy {
  @Input() control: FormControlBase<any>;
  @Input() form: FormGroup;
  @Output() onClickEventRaised: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDropdownChangeRaised: EventEmitter<any> = new EventEmitter<any>();
  @Output() onOverrideActionExecuted: EventEmitter<any> = new EventEmitter<any>();

  public overridenValue: any;
  private unsubscribe: Subject<boolean> = new Subject();

  public ngOnInit(): void {
    if (this.form && this.control) {
      this.control.formGroup = this.form;

      if (this.control.overrideObservable) {
        this.control.overrideObservable
          .pipe(takeUntil(this.unsubscribe)).subscribe(newValue => {
            this.override(newValue);
          });
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public get isValid(): boolean {
    return this.form.controls[this.control.key].valid;
  }

  private get isCheckbox(): boolean {
    return this.control.controlType && this.control.controlType.toLowerCase() === 'checkbox';
  }

  private get isFilterDropdown(): boolean {
    return this.control.controlType && this.control.controlType.toLowerCase() === 'filter-dropdown';
  }

  public onClickEvent(): void {
    this.onClickEventRaised.emit();
  }

  public onDropdownChange(event: any): void {
    this.onDropdownChangeRaised.emit(event);
  }

  public overrideValue(): void {
    if ((this.control.previousValue) || (this.control.previousValue === '')) {
      this.setFormControlValue(this.control.previousValue);
      this.setPreviousValue(null);
    } else {
      // Perform the calculation
      this.setOverrideInProgress(true);
      this.control.overrideAction().pipe(takeUntil(this.unsubscribe)).subscribe(result => {
        this.setOverrideInProgress(false);
        this.onOverrideActionExecuted.emit(result);
        if (result && result.resultValue !== null) {
          this.override(result.resultValue);
        }

        if (this.isFilterDropdown) {
          (this.control as FilterDropdownControl)
            .overrideFilterDropdown(result, this.control);
        }
      });
    }
  }

  private setOverrideInProgress(inProgress: boolean): void {
    this.control.disabled = inProgress;
    this.control.overridingInProgress = inProgress;
  }

  public override(newValue: any): void {
    if (this.control.previousValue) {
      return;
    }

    if (this.isCheckbox) {
      this.overrideCheckboxValue(newValue);
    } else {
      this.overrideCommonValue(newValue);
    }
  }

  public overrideCheckboxValue(newValue: any): void {
    // store prev value as string because of control.previousValue type declaration
    // is checkbox is not checked and the prev value should be assigned, the prev value should be '0'
    this.setPreviousValue(isTrueValue(this.control.value) ? '1' : '0');
    this.setCheckboxFormControlValue(newValue);
  }

  public overrideCommonValue(newValue: any): void {
    // Cache the previous value so we can restore it when the "clear" button is clicked
    this.setPreviousValue(this.control.formControl.value);
    // Set the new value
    this.control.formControl.setValue(newValue.toUpperCase());
  }

  private setPreviousValue(value: string): void {
    this.control.previousValue = value;
  }

  private setCheckboxFormControlValue = (value: any): void => this.control.formControl.setValue(isTrueValue(value));

  private setFormControlValue(value: any): void {
    if (this.isCheckbox) {
      this.setCheckboxFormControlValue(value);
    } else {
      this.control.formControl.setValue(value);
    }
  }
}
